import type React from 'react';
import { useEffect, useState } from 'react';
import { useOverlayGrant } from '@hooks/useOverlayGrant';
import { LoginPromptModal } from '@components/LoginPromptModal';
import { useAuth } from '@providers';
import { getUserInitials } from '@utils/userAvatar';
import { POLL_COPY } from './pollCopy';
import {
  closePollSignInForSignIn,
  dismissPollSignIn,
  pollPickWillReplay,
  usePollSignInPrompt,
  type PollSignInPrompt,
} from './pollSignIn';

/** B7's Space avatar: 56px, 14px radius, the Space's image or its initials on yellow. */
const PlaceAvatar: React.FC<{ name: string; imageUrl: string | null }> = ({ name, imageUrl }) =>
  imageUrl ? (
    <img
      src={imageUrl}
      alt=""
      aria-hidden="true"
      className="size-14 shrink-0 rounded-[14px] object-cover"
      data-testid="poll-sign-in-avatar"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex size-14 shrink-0 items-center justify-center rounded-[14px] bg-mb-yellow text-xl leading-6 font-semibold text-mb-ink"
      data-testid="poll-sign-in-avatar"
    >
      {getUserInitials(name)}
    </span>
  );

/**
 * The sign-in drawer for an answer saved signed out (TECH-197 B7, OPTION 1 · 1c): today's auth
 * prompt (`LoginPromptModal`) with the pick in it: the poll's Space avatar, "Your answer is saved",
 * "You picked {option}. It will count once you sign in." and "Sign in to submit your answer"; no
 * other link. Mounted once at the root (`RootLayout`), so it outlives the Post sheet the tap closed.
 * Sign in stores the `VOTE_POLL` intent and goes through today's sign-in (`pollSignIn`). Closing it
 * keeps the pick ticked in this tab (E5) with nothing to replay. Holds an overlay grant, so Android
 * Back closes it, not the Post sheet under it.
 */
export const PollSignInPromptHost: React.FC = () => {
  const open = usePollSignInPrompt();
  // The last prompt stays drawn while the drawer slides away, so it never flashes the stock words.
  const [prompt, setPrompt] = useState(open);
  if (open !== null && open !== prompt) setPrompt(open);
  // Nothing at all (no auth read) until a guest first answers.
  return prompt ? <PollSignInDrawer open={open !== null} prompt={prompt} /> : null;
};

const PollSignInDrawer: React.FC<{ open: boolean; prompt: PollSignInPrompt }> = ({
  open,
  prompt,
}) => {
  const { signIn } = useAuth();
  // Android Back closes B7 (the overlay mutex), never the Post sheet under it (FE-1d review).
  const grant = useOverlayGrant('poll-sign-in', {
    onBackButton: held => {
      held.dismiss('back-button');
      dismissPollSignIn();
    },
  });
  // The prompt store is the external state; the grant follows it.
  useEffect(() => {
    if (open) grant.show();
    else grant.dismiss('programmatic');
  }, [open, grant]);
  return (
    <LoginPromptModal
      isOpen={open && grant.open}
      actionType="answer_poll"
      onClose={dismissPollSignIn}
      onLogin={() => {
        // The pick becomes the replayable intent only now (FE-1d review), before sign-in starts;
        // drawn at the root, the drawer would otherwise stay over the sign-in door.
        closePollSignInForSignIn();
        signIn({ actionType: 'answer_poll' });
      }}
      content={{
        media: prompt.place ? (
          <PlaceAvatar name={prompt.place.name} imageUrl={prompt.place.imageUrl} />
        ) : null,
        title: POLL_COPY.answerSaved,
        // Another pending intent keeps its place, and then the pick is not replayed (FE-L1a review).
        description: pollPickWillReplay()
          ? POLL_COPY.youPickedSignIn(prompt.optionTexts)
          : POLL_COPY.youPicked(prompt.optionTexts),
        buttonLabel: POLL_COPY.signInToSubmitAnswer,
      }}
    />
  );
};
